import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ReminderItem, ReminderCategory, ReminderPriority, ReminderRepeat, ReminderOffset, ReminderOffsetUnit } from '../types';
import { 
  subscribeToReminders, 
  addReminder, 
  updateReminder, 
  deleteReminder, 
  toggleReminderStatus,
  clearAllReminders,
  subscribeToFamilyMembers,
  saveFamilyMembers,
  getFamilyMembers,
  DEFAULT_FAMILY_MEMBERS
} from '../lib/reminderStorage';
import { 
  Bell, Calendar as CalendarIcon, CheckCircle2, Circle, Clock, Plus, Trash2, 
  Edit3, Filter, User, Stethoscope, ShoppingBag, FileText, Users, Tag, AlertTriangle, 
  Sparkles, ChevronRight, ChevronLeft, Search, Check, RefreshCw, X, ShieldAlert,
  Download, Upload, ExternalLink, Cake, PartyPopper, CreditCard, GraduationCap, Gift, Receipt
} from 'lucide-react';
import { toPersianDigits } from '../lib/storage';
import { useLanguage, formatNumberByLang, Language } from '../lib/i18n';
import VoiceInputButton from './VoiceInputButton';
import { formatGregorianDate } from '../lib/dateUtils';
import { 
  createReminderGoogleCalendarUrl, 
  generateFamilyRemindersIcsContent, 
  parseIcsToReminders,
  downloadIcsFile 
} from '../lib/calendar';

interface ReminderModuleProps {
  currentDate?: string;
  onSetDate?: (dateStr: string) => void;
}

// Helper to format reminder offset into current language
export const formatReminderOffset = (
  offset: ReminderOffset,
  lang: Language,
  t: (key: string, defaultVal?: string, params?: any) => string
): string => {
  const { value, unit } = offset;
  if (value === 0) {
    return t('offsetAtTime', lang === 'sv' ? 'Vid förfallotid' : lang === 'en' ? 'At due time' : 'همان لحظه سررسید');
  }

  const num = formatNumberByLang(value, lang);

  if (lang === 'sv') {
    switch (unit) {
      case 'minutes':
        return value === 1 ? '1 minut innan' : `${num} minuter innan`;
      case 'hours':
        return value === 1 ? '1 timme innan' : `${num} timmar innan`;
      case 'days':
        return value === 1 ? '1 dag innan' : `${num} dagar innan`;
      case 'weeks':
        return value === 1 ? '1 vecka innan' : `${num} veckor innan`;
    }
  }

  if (lang === 'en') {
    switch (unit) {
      case 'minutes':
        return value === 1 ? '1 minute before' : `${num} minutes before`;
      case 'hours':
        return value === 1 ? '1 hour before' : `${num} hours before`;
      case 'days':
        return value === 1 ? '1 day before' : `${num} days before`;
      case 'weeks':
        return value === 1 ? '1 week before' : `${num} weeks before`;
    }
  }

  // Default Persian (fa)
  const unitName =
    unit === 'minutes' ? t('offsetMin', 'دقیقه') :
    unit === 'hours' ? t('offsetHour', 'ساعت') :
    unit === 'days' ? t('offsetDay', 'روز') : t('offsetWeek', 'هفته');
  return `${num} ${unitName} ${t('offsetBefore', 'قبل')}`;
};

export default function ReminderModule({ currentDate, onSetDate }: ReminderModuleProps) {
  const { lang, t } = useLanguage();
  const [reminders, setReminders] = useState<ReminderItem[]>([]);
  const [activeTab, setActiveTab] = useState<'today_upcoming' | 'calendar' | 'categories'>('today_upcoming');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedPerson, setSelectedPerson] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Quick Add Modal & Edit Modal
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingReminder, setEditingReminder] = useState<ReminderItem | null>(null);

  // Form states
  const todayStr = new Date().toISOString().split('T')[0];
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ReminderCategory>('medical');
  const [person, setPerson] = useState(DEFAULT_FAMILY_MEMBERS[0] || 'من');
  const [customPerson, setCustomPerson] = useState('');
  const [date, setDate] = useState(todayStr);
  const [time, setTime] = useState('10:00');
  const [repeat, setRepeat] = useState<ReminderRepeat>('none');
  const [priority, setPriority] = useState<ReminderPriority>('medium');

  // Reminder Offsets State
  const [offsets, setOffsets] = useState<ReminderOffset[]>([{ value: 1, unit: 'hours' }]);
  const [customOffsetValue, setCustomOffsetValue] = useState<number>(1);
  const [customOffsetUnit, setCustomOffsetUnit] = useState<ReminderOffsetUnit>('days');

  // ICS File Input Ref
  const icsFileRef = useRef<HTMLInputElement | null>(null);

  // Helper to calculate exact notification date and time relative to due date and time
  const calculateNotificationTime = (dueDate: string, dueTime: string | undefined, offset: ReminderOffset) => {
    const dStr = dueDate || new Date().toISOString().split('T')[0];
    const tStr = dueTime || '09:00';
    const [year, month, day] = dStr.split('-').map(Number);
    const [hours, minutes] = tStr.split(':').map(Number);

    const due = new Date(year, month - 1, day, hours, minutes, 0);

    let msToSubtract = 0;
    switch (offset.unit) {
      case 'minutes':
        msToSubtract = offset.value * 60 * 1000;
        break;
      case 'hours':
        msToSubtract = offset.value * 60 * 60 * 1000;
        break;
      case 'days':
        msToSubtract = offset.value * 24 * 60 * 60 * 1000;
        break;
      case 'weeks':
        msToSubtract = offset.value * 7 * 24 * 60 * 60 * 1000;
        break;
    }

    const notif = new Date(due.getTime() - msToSubtract);

    const nYear = notif.getFullYear();
    const nMonth = String(notif.getMonth() + 1).padStart(2, '0');
    const nDay = String(notif.getDate()).padStart(2, '0');
    const nHours = String(notif.getHours()).padStart(2, '0');
    const nMinutes = String(notif.getMinutes()).padStart(2, '0');

    const notifDateStr = `${nYear}-${nMonth}-${nDay}`;
    const notifTimeStr = `${nHours}:${nMinutes}`;
    const formattedDate = formatGregorianDate(notifDateStr, lang, 'medium');

    return {
      dateStr: notifDateStr,
      timeStr: notifTimeStr,
      fullFormatted: `${formattedDate} — ${t('reminderCardTime', 'ساعت {{time}}', { time: notifTimeStr })}`
    };
  };

  // Toggle quick preset offset
  const handleToggleOffset = (target: ReminderOffset) => {
    const exists = offsets.some(o => o.value === target.value && o.unit === target.unit);
    if (exists) {
      setOffsets(offsets.filter(o => !(o.value === target.value && o.unit === target.unit)));
    } else {
      setOffsets([...offsets, target]);
    }
  };

  // Add custom offset rule
  const handleAddCustomOffset = () => {
    const val = Math.max(0, customOffsetValue);
    const exists = offsets.some(o => o.value === val && o.unit === customOffsetUnit);
    if (!exists) {
      setOffsets([...offsets, { value: val, unit: customOffsetUnit }]);
    }
  };

  // Remove offset rule
  const handleRemoveOffset = (index: number) => {
    setOffsets(offsets.filter((_, idx) => idx !== index));
  };

  // Handle ICS File Import
  const handleImportIcs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target?.result as string;
      if (!content) return;

      const parsedItems = parseIcsToReminders(content);
      if (parsedItems.length === 0) {
        alert(t('reminderImportNoItems', 'هیچ رویداد قابل پردازشی در این فایل تقویم یافت نشد.'));
        return;
      }

      let count = 0;
      for (const item of parsedItems) {
        if (item.title) {
          await addReminder({
            title: item.title,
            description: item.description || '',
            category: item.category || 'personal',
            person: item.person || 'من',
            date: item.date || todayStr,
            time: item.time || '09:00',
            repeat: item.repeat || 'none',
            priority: item.priority || 'medium',
            reminders: item.reminders || [{ value: 1, unit: 'hours' }],
            status: 'pending'
          });
          count++;
        }
      }

      alert(t('reminderImportSuccess', 'با موفقیت {{count}} یادآور همراه با زمان‌بندی هشدارها از فایل تقویم وارد شدند.', { count: toPersianDigits(count) }));
      if (icsFileRef.current) icsFileRef.current.value = '';
    };

    reader.readAsText(file);
  };

  // Calendar View State
  const [calendarYear, setCalendarYear] = useState<number>(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState<number>(new Date().getMonth()); // 0-indexed
  const [selectedCalDate, setSelectedCalDate] = useState<string>(todayStr);

  // Dynamic Family Members State & Modal
  const [familyMembers, setFamilyMembers] = useState<string[]>([]);
  const [showFamilyModal, setShowFamilyModal] = useState<boolean>(false);
  const [newMemberName, setNewMemberName] = useState<string>('');

  // Subscribe to real-time Cloudflare D1 reminders & Family Members
  useEffect(() => {
    const unsubReminders = subscribeToReminders((items) => {
      setReminders(items);
    });
    const unsubFamily = subscribeToFamilyMembers((members) => {
      setFamilyMembers(members);
    });
    return () => {
      unsubReminders();
      unsubFamily();
    };
  }, []);

  // Filtered Persons List based on managed family members
  const availablePersons = useMemo(() => {
    const base = familyMembers.length > 0 ? familyMembers : DEFAULT_FAMILY_MEMBERS;
    const custom = reminders.map(r => r.person).filter(p => p && !base.includes(p));
    return Array.from(new Set([...base, ...custom]));
  }, [familyMembers, reminders]);

  // Handle Family Member Addition
  const handleAddFamilyMember = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = newMemberName.trim();
    if (!trimmed) return;
    if (familyMembers.includes(trimmed)) {
      alert(t('reminderFamilyExist', 'این نام قبلاً در لیست افراد ثبت شده است.'));
      return;
    }
    const updated = [...familyMembers, trimmed];
    setFamilyMembers(updated);
    saveFamilyMembers(updated);
    setNewMemberName('');
  };

  // Handle Family Member Deletion
  const handleRemoveFamilyMember = (memberToRemove: string) => {
    if (memberToRemove === 'من' || memberToRemove === t('reminderMe', 'من')) {
      alert(t('reminderCantRemoveMe', 'حذف فرد «من» امکان‌پذیر نیست.'));
      return;
    }
    const updated = familyMembers.filter(m => m !== memberToRemove);
    setFamilyMembers(updated);
    saveFamilyMembers(updated);
    if (selectedPerson === memberToRemove) {
      setSelectedPerson('all');
    }
  };

  // Open Add Modal
  const handleOpenAdd = (defaultCat?: ReminderCategory) => {
    const targetCat = defaultCat || 'birthday';
    setEditingReminder(null);
    setTitle('');
    setDescription('');
    setCategory(targetCat);
    setPerson(availablePersons[0] || t('reminderMe', 'من'));
    setCustomPerson('');
    setDate(todayStr);
    setTime('10:00');
    setRepeat(targetCat === 'birthday' ? 'yearly' : 'none');
    setPriority('medium');
    setOffsets([
      { value: 1, unit: 'days' },
      { value: 1, unit: 'hours' }
    ]);
    setShowModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (reminder: ReminderItem) => {
    setEditingReminder(reminder);
    setTitle(reminder.title);
    setDescription(reminder.description || '');
    setCategory(reminder.category);
    if (availablePersons.includes(reminder.person)) {
      setPerson(reminder.person);
      setCustomPerson('');
    } else {
      setPerson('custom');
      setCustomPerson(reminder.person);
    }
    setDate(reminder.date);
    setTime(reminder.time || '10:00');
    setRepeat(reminder.repeat);
    setPriority(reminder.priority);
    setOffsets(reminder.reminders && reminder.reminders.length > 0 ? reminder.reminders : [{ value: 1, unit: 'hours' }]);
    setShowModal(true);
  };

  // Submit Add/Edit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const targetPerson = person === 'custom' ? (customPerson.trim() || t('reminderUnknown', 'نامشخص')) : person;

    if (editingReminder) {
      await updateReminder({
        ...editingReminder,
        title: title.trim(),
        description: description.trim(),
        category,
        person: targetPerson,
        date,
        time,
        repeat,
        priority,
        reminders: offsets
      });
    } else {
      await addReminder({
        title: title.trim(),
        description: description.trim(),
        category,
        person: targetPerson,
        date,
        time,
        repeat,
        priority,
        reminders: offsets,
        status: 'pending'
      });
    }

    setShowModal(false);
  };

  // Quick preset templates for rapid creation
  const handleQuickPreset = (presetTitle: string, presetCat: ReminderCategory, defaultRepeat: ReminderRepeat = 'none') => {
    setTitle(presetTitle);
    setCategory(presetCat);
    setRepeat(defaultRepeat);
  };

  // Category Colors & Icons Helper
  const getCategoryMeta = (cat: ReminderCategory) => {
    switch (cat) {
      case 'birthday':
        return {
          name: t('reminderCatBirthday', 'تولد و سالگردها'),
          bg: 'bg-fuchsia-50 dark:bg-fuchsia-950/40 border-fuchsia-200 dark:border-fuchsia-900',
          text: 'text-fuchsia-700 dark:text-fuchsia-300',
          badge: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/60 dark:text-fuchsia-200',
          icon: Cake
        };
      case 'event':
        return {
          name: t('reminderCatEvent', 'رویداد و قرار ملاقات'),
          bg: 'bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-900',
          text: 'text-sky-700 dark:text-sky-300',
          badge: 'bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-200',
          icon: PartyPopper
        };
      case 'bills':
        return {
          name: t('reminderCatBills', 'قبوض و مالی'),
          bg: 'bg-teal-50 dark:bg-teal-950/40 border-teal-200 dark:border-teal-900',
          text: 'text-teal-700 dark:text-teal-300',
          badge: 'bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-200',
          icon: CreditCard
        };
      case 'school':
        return {
          name: t('reminderCatSchool', 'مدرسه و آموزش'),
          bg: 'bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-900',
          text: 'text-violet-700 dark:text-violet-300',
          badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-200',
          icon: GraduationCap
        };
      case 'medical':
        return {
          name: t('reminderCatMedical', 'پزشکی و سلامت'),
          bg: 'bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-900',
          text: 'text-rose-700 dark:text-rose-300',
          badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200',
          icon: Stethoscope
        };
      case 'shopping':
        return {
          name: t('reminderCatShopping', 'خرید و مرسولات'),
          bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900',
          text: 'text-amber-700 dark:text-amber-300',
          badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
          icon: ShoppingBag
        };
      case 'personal':
        return {
          name: t('reminderCatPersonal', 'امور شخصی و اداری'),
          bg: 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-200 dark:border-indigo-900',
          text: 'text-indigo-700 dark:text-indigo-300',
          badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200',
          icon: FileText
        };
      case 'family':
        return {
          name: t('reminderCatFamily', 'مدیریت خانواده'),
          bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-900',
          text: 'text-emerald-700 dark:text-emerald-300',
          badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
          icon: Users
        };
    }
  };

  const getPriorityBadge = (p: ReminderPriority) => {
    switch (p) {
      case 'high':
        return <span className="px-2 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center gap-1">{t('reminderPriHigh', 'فوری')}</span>;
      case 'medium':
        return <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">{t('reminderPriMed', 'متوسط')}</span>;
      case 'low':
        return <span className="px-2 py-0.5 rounded-full bg-slate-400 text-white text-[10px] font-medium">{t('reminderPriLow', 'عادی')}</span>;
    }
  };

  // Filtered Reminders
  const filteredReminders = useMemo(() => {
    return reminders.filter(r => {
      if (selectedCategory !== 'all' && r.category !== selectedCategory) return false;
      if (selectedPerson !== 'all' && r.person !== selectedPerson) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = r.title.toLowerCase().includes(q);
        const matchDesc = (r.description || '').toLowerCase().includes(q);
        const matchPerson = r.person.toLowerCase().includes(q);
        if (!matchTitle && !matchDesc && !matchPerson) return false;
      }
      return true;
    });
  }, [reminders, selectedCategory, selectedPerson, searchQuery]);

  // Split into Today vs Upcoming vs Past
  const todayReminders = useMemo(() => {
    return filteredReminders.filter(r => r.date === todayStr);
  }, [filteredReminders, todayStr]);

  const upcomingReminders = useMemo(() => {
    return filteredReminders.filter(r => r.date > todayStr);
  }, [filteredReminders, todayStr]);

  const pastReminders = useMemo(() => {
    return filteredReminders.filter(r => r.date < todayStr);
  }, [filteredReminders, todayStr]);

  // Calendar Days Calculation
  const calendarDays = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sun

    const days = [];
    // Padding before 1st
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const monthStr = String(calendarMonth + 1).padStart(2, '0');
      const dayStr = String(d).padStart(2, '0');
      const dateString = `${calendarYear}-${monthStr}-${dayStr}`;
      
      const dayReminders = reminders.filter(r => r.date === dateString);
      days.push({
        day: d,
        dateString,
        reminders: dayReminders
      });
    }
    return days;
  }, [calendarYear, calendarMonth, reminders]);

  const monthNamesPersian = [
    t('month1', 'ژانویه (دی/بهمن)'), t('month2', 'فوریه (بهمن/اسفند)'), t('month3', 'مارس (اسفند/فروردین)'), t('month4', 'آوریل (فروردین/اردیبهشت)'),
    t('month5', 'می (اردیبهشت/خرداد)'), t('month6', 'ژوئن (خرداد/تیر)'), t('month7', 'ژوئیه (تیر/مرداد)'), t('month8', 'اوت (مرداد/شهریور)'),
    t('month9', 'سپتامبر (شهریور/مهر)'), t('month10', 'اکتبر (مهر/آبان)'), t('month11', 'نوامبر (آبان/آذر)'), t('month12', 'دسامبر (آذر/دی)')
  ];

  return (
    <div className="space-y-5 pb-24 md:pb-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-900 rounded-3xl p-5 sm:p-6 text-white shadow-xl relative overflow-hidden">
        <div className="absolute -left-8 -bottom-8 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur-md text-xs font-semibold text-indigo-100 flex items-center gap-1">
                <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                {t('remindersTitle', 'دفترچه یادآور خانوادگی')}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black font-display tracking-tight flex items-center gap-2">
              <Bell className="w-7 h-7 text-amber-400 shrink-0" />
              <span>{t('reminderHeaderTitle', 'دفترچه یادآور هوشمند خانوادگی و برنامه‌های سررسید')}</span>
            </h1>
            <p className="text-xs sm:text-sm text-indigo-100/90 mt-1 max-w-xl">
              {t('reminderHeaderDesc', 'ثبت یادآورهای پزشکی، قبوض، تولدها و امور مدرسه همراه با همگام‌سازی ابری و خروجی تقویم')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 self-start sm:self-center shrink-0">
            {reminders.length > 0 && (
              <button
                onClick={async () => {
                  if (window.confirm(t('reminderClearAllConfirm', 'آیا از حذف کامل تمام یادآورهای خانوادگی اطمینان دارید؟ این عمل غیرقابل بازگشت است.'))) {
                    await clearAllReminders();
                    setReminders([]);
                  }
                }}
                className="px-3.5 py-3 rounded-2xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 hover:text-white font-bold text-xs sm:text-sm border border-rose-400/30 transition-all flex items-center gap-1.5 cursor-pointer backdrop-blur-md"
                title={t('reminderClearAllTitle', 'پاکسازی کامل تمام یادآورهای فعلی')}
              >
                <Trash2 className="w-4 h-4" />
                <span>{t('reminderClearAllBtn', 'پاکسازی همه')}</span>
              </button>
            )}

            <button
              onClick={() => {
                const icsContent = generateFamilyRemindersIcsContent(reminders);
                if (!icsContent) {
                  alert(t('reminderNoIcsError', 'هیچ یادآور فعالی برای خروجی تقویم وجود ندارد.'));
                  return;
                }
                downloadIcsFile('family-reminders.ics', icsContent);
              }}
              className="px-4 py-3 rounded-2xl bg-white/15 hover:bg-white/25 text-white font-extrabold text-xs sm:text-sm border border-white/20 transition-all flex items-center gap-1.5 cursor-pointer backdrop-blur-md"
              title={t('reminderDownloadIcsTitle', 'دانلود تمام یادآورهای فعال در قالب فایل تقویم .ics جهت همگام‌سازی روی آیفون، سامسونگ یا گوگل کلندر')}
            >
              <Download className="w-4 h-4" />
              <span>{t('reminderDownloadIcsBtn', 'دانلود تقویم (.ics)')}</span>
            </button>

            <input
              type="file"
              ref={icsFileRef}
              accept=".ics,text/calendar"
              onChange={handleImportIcs}
              className="hidden"
            />

            <button
              onClick={() => icsFileRef.current?.click()}
              className="px-4 py-3 rounded-2xl bg-white/15 hover:bg-white/25 text-white font-extrabold text-xs sm:text-sm border border-white/20 transition-all flex items-center gap-1.5 cursor-pointer backdrop-blur-md"
              title={t('reminderImportIcsTitle', 'ورود فایل تقویم .ics و بارگذاری یادآورها همراه با هشدارهای تعیین‌شده')}
            >
              <Upload className="w-4 h-4" />
              <span>{t('reminderImportIcsBtn', 'ورود تقویم (.ics)')}</span>
            </button>

            <button
              onClick={() => handleOpenAdd()}
              className="px-5 py-3 rounded-2xl bg-amber-400 hover:bg-amber-300 text-indigo-950 font-extrabold text-xs sm:text-sm shadow-lg hover:shadow-xl transition-all flex items-center gap-2 transform active:scale-95 cursor-pointer"
            >
              <Plus className="w-5 h-5 stroke-[2.5]" />
              <span>{t('reminderAddBtn', 'ثبت یادآور جدید')}</span>
            </button>
          </div>
        </div>

        {/* Quick Stats Pill */}
        <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5">
            <span className="text-indigo-200 block text-[11px]">{t('reminderStatsToday', 'یادآورهای امروز')}</span>
            <span className="text-lg font-black text-white">{toPersianDigits(todayReminders.length)}</span>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5">
            <span className="text-indigo-200 block text-[11px]">{t('reminderStatsUpcoming', 'پیش‌رو در روزهای آینده')}</span>
            <span className="text-lg font-black text-amber-300">{toPersianDigits(upcomingReminders.length)}</span>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5">
            <span className="text-indigo-200 block text-[11px]">{t('reminderStatsMed', 'کارهای پزشکی و دارویی')}</span>
            <span className="text-lg font-black text-rose-300">
              {toPersianDigits(reminders.filter(r => r.category === 'medical' && r.status === 'pending').length)}
            </span>
          </div>
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5">
            <span className="text-indigo-200 block text-[11px]">{t('reminderStatsDone', 'انجام‌شده')}</span>
            <span className="text-lg font-black text-emerald-300">
              {toPersianDigits(reminders.filter(r => r.status === 'completed').length)}
            </span>
          </div>
        </div>
      </div>

      {/* Main View Navigation Tabs */}
      <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800/80 p-1.5 rounded-2xl border border-gray-200 dark:border-gray-700/60 overflow-x-auto">
        <button
          onClick={() => setActiveTab('today_upcoming')}
          className={`flex-1 min-w-[110px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'today_upcoming'
              ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Clock className="w-4 h-4" />
          <span>{t('reminderTabToday', 'امروز و پیش‌رو')}</span>
        </button>

        <button
          onClick={() => setActiveTab('calendar')}
          className={`flex-1 min-w-[110px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'calendar'
              ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <CalendarIcon className="w-4 h-4" />
          <span>{t('reminderTabCalendar', 'تقویم ماهانه')}</span>
        </button>

        <button
          onClick={() => setActiveTab('categories')}
          className={`flex-1 min-w-[110px] py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'categories'
              ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Tag className="w-4 h-4" />
          <span>{t('reminderTabCategories', 'دسته‌بندی‌ها')}</span>
        </button>
      </div>

      {/* Filters bar: Search & Person & Category */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('reminderSearchPh', 'جستجو در عنوان یادآور، شخص یا توضیحات...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pr-10 pl-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xs"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Person Filter */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 shrink-0">
            <span className="text-xs text-gray-500 font-medium shrink-0 flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {t('reminderPersonFilter', 'فرد:')}
            </span>
            <button
              onClick={() => setSelectedPerson('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                selectedPerson === 'all'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
              }`}
            >
              {t('reminderAllPersons', 'همه افراد')}
            </button>
            {availablePersons.map(p => (
              <button
                key={p}
                onClick={() => setSelectedPerson(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  selectedPerson === p
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
                }`}
              >
                {p}
              </button>
            ))}

            <button
              onClick={() => setShowFamilyModal(true)}
              className="px-2.5 py-1.5 rounded-lg text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900 transition-all flex items-center gap-1 shrink-0 cursor-pointer"
              title={t('reminderManageMembersTitle', 'مدیریت، افزودن یا حذف اعضای خانواده')}
            >
              <Users className="w-3.5 h-3.5" />
              <span>{t('reminderManageMembersBtn', 'مدیریت اعضا')}</span>
            </button>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pt-1 pb-1">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
              selectedCategory === 'all'
                ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
            }`}
          >
            {t('reminderAllCatsCount', 'همه دسته‌ها ({{count}})', { count: toPersianDigits(reminders.length) })}
          </button>

          {(['birthday', 'event', 'bills', 'school', 'medical', 'shopping', 'personal', 'family'] as ReminderCategory[]).map(cat => {
            const meta = getCategoryMeta(cat);
            const CatIcon = meta.icon;
            const isSelected = selectedCategory === cat;
            const catCount = reminders.filter(r => r.category === cat).length;

            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer border ${
                  isSelected
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                    : `${meta.bg} ${meta.text}`
                }`}
              >
                <CatIcon className="w-3.5 h-3.5 shrink-0" />
                <span>{meta.name}</span>
                {catCount > 0 && (
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${isSelected ? 'bg-white/20 text-white' : meta.badge}`}>
                    {toPersianDigits(catCount)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* VIEW 1: Today & Upcoming */}
      {activeTab === 'today_upcoming' && (
        <div className="space-y-6">
          {/* Today Section */}
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                <h2 className="text-lg font-extrabold font-display text-gray-900 dark:text-white flex items-center gap-2">
                  <span>{t('reminderTodayTitle', 'امروز')}</span>
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({todayStr})</span>
                </h2>
                <span className="px-2.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 text-xs font-bold">
                  {t('reminderItemsCount', '{{count}} مورد', { count: toPersianDigits(todayReminders.length) })}
                </span>
              </div>

              <button
                onClick={() => handleOpenAdd()}
                className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>{t('reminderAddShort', 'افزودن')}</span>
              </button>
            </div>

            {todayReminders.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 space-y-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto opacity-80" />
                <p className="text-xs sm:text-sm font-medium">{t('reminderNoTodayMsg', 'امروز هیچ یادآور معوقی ندارید!')}</p>
                <p className="text-xs text-gray-400">{t('reminderNoTodayDesc', 'یک یادآور جدید برای قرارهای امروز ثبت کنید.')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {todayReminders.map(rem => (
                  <ReminderCard 
                    key={rem.id} 
                    reminder={rem} 
                    getCategoryMeta={getCategoryMeta} 
                    getPriorityBadge={getPriorityBadge}
                    onToggleStatus={() => toggleReminderStatus(rem.id)}
                    onEdit={() => handleOpenEdit(rem)}
                    onDelete={() => deleteReminder(rem.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Section */}
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-4">
            <div className="flex items-center justify-between border-b pb-3 border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-amber-500" />
                <h2 className="text-lg font-extrabold font-display text-gray-900 dark:text-white">
                  {t('reminderUpcomingTitle', 'روزهای آینده و پیش‌رو')}
                </h2>
                <span className="px-2.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 text-xs font-bold">
                  {t('reminderItemsCount', '{{count}} مورد', { count: toPersianDigits(upcomingReminders.length) })}
                </span>
              </div>
            </div>

            {upcomingReminders.length === 0 ? (
              <div className="text-center py-6 text-gray-400 text-xs">
                {t('reminderNoUpcoming', 'هیچ یادآوری برای روزهای آینده ثبت نشده است.')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {upcomingReminders.map(rem => (
                  <ReminderCard 
                    key={rem.id} 
                    reminder={rem} 
                    getCategoryMeta={getCategoryMeta} 
                    getPriorityBadge={getPriorityBadge}
                    onToggleStatus={() => toggleReminderStatus(rem.id)}
                    onEdit={() => handleOpenEdit(rem)}
                    onDelete={() => deleteReminder(rem.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Past / Completed Section (Collapsible) */}
          {pastReminders.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-900/40 rounded-3xl p-5 border border-gray-200/60 dark:border-gray-800 space-y-4">
              <div className="flex items-center justify-between border-b pb-3 border-gray-200 dark:border-gray-800">
                <h3 className="text-sm font-bold text-gray-600 dark:text-gray-400">
                  {t('reminderPastTitle', 'یادآورهای گذشته ({{count}})', { count: toPersianDigits(pastReminders.length) })}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-80">
                {pastReminders.map(rem => (
                  <ReminderCard 
                    key={rem.id} 
                    reminder={rem} 
                    getCategoryMeta={getCategoryMeta} 
                    getPriorityBadge={getPriorityBadge}
                    onToggleStatus={() => toggleReminderStatus(rem.id)}
                    onEdit={() => handleOpenEdit(rem)}
                    onDelete={() => deleteReminder(rem.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: Calendar View */}
      {activeTab === 'calendar' && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 space-y-5">
          {/* Calendar Month Selector */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                if (calendarMonth === 0) {
                  setCalendarMonth(11);
                  setCalendarYear(calendarYear - 1);
                } else {
                  setCalendarMonth(calendarMonth - 1);
                }
              }}
              className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-pointer"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="text-center">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                {monthNamesPersian[calendarMonth]} {toPersianDigits(calendarYear)}
              </h3>
            </div>

            <button
              onClick={() => {
                if (calendarMonth === 11) {
                  setCalendarMonth(0);
                  setCalendarYear(calendarYear + 1);
                } else {
                  setCalendarMonth(calendarMonth + 1);
                }
              }}
              className="p-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-pointer"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>

          {/* Days of Week Header */}
          <div className="grid grid-cols-7 text-center text-xs font-bold text-gray-400 pb-2 border-b border-gray-100 dark:border-gray-800">
            <div>{t('daySun', 'یکشنبه')}</div>
            <div>{t('dayMon', 'دوشنبه')}</div>
            <div>{t('dayTue', 'سه‌شنبه')}</div>
            <div>{t('dayWed', 'چهارشنبه')}</div>
            <div>{t('dayThu', 'پنج‌شنبه')}</div>
            <div>{t('dayFri', 'جمعه')}</div>
            <div>{t('daySat', 'شنبه')}</div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {calendarDays.map((item, idx) => {
              if (!item) {
                return <div key={`empty-${idx}`} className="h-16 sm:h-20 bg-transparent rounded-xl" />;
              }

              const isSelected = item.dateString === selectedCalDate;
              const isToday = item.dateString === todayStr;
              const count = item.reminders.length;

              return (
                <button
                  key={item.dateString}
                  onClick={() => setSelectedCalDate(item.dateString)}
                  className={`h-16 sm:h-20 p-1 sm:p-2 rounded-2xl flex flex-col justify-between items-center transition-all border cursor-pointer ${
                    isSelected
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-md transform scale-105 z-10'
                      : isToday
                      ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-700'
                      : 'bg-gray-50/70 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border-gray-100 dark:border-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <span className={`text-xs sm:text-sm font-black ${isSelected ? 'text-white' : ''}`}>
                    {toPersianDigits(item.day)}
                  </span>

                  {count > 0 && (
                    <div className="flex items-center gap-0.5 max-w-full overflow-hidden">
                      {item.reminders.slice(0, 3).map((r, rIdx) => {
                        const meta = getCategoryMeta(r.category);
                        return (
                          <span 
                            key={rIdx} 
                            className={`w-2 h-2 rounded-full ${isSelected ? 'bg-amber-300' : 'bg-indigo-500'}`} 
                          />
                        );
                      })}
                      {count > 3 && (
                        <span className={`text-[9px] font-bold ${isSelected ? 'text-indigo-200' : 'text-gray-500'}`}>
                          +{count - 3}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Selected Date Reminders */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-800 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-indigo-500" />
                <span>{t('reminderSelectedDate', 'یادآورهای روز {{date}}', { date: formatGregorianDate(selectedCalDate, lang, 'medium') })}</span>
              </h4>
              <button
                onClick={() => {
                  setDate(selectedCalDate);
                  handleOpenAdd();
                }}
                className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>{t('reminderAddForDate', 'افزودن برای این تاریخ')}</span>
              </button>
            </div>

            {reminders.filter(r => r.date === selectedCalDate).length === 0 ? (
              <p className="text-xs text-gray-400 py-3 text-center">
                {t('reminderNoForDate', 'هیچ یادآوری برای تاریخ {{date}} ثبت نشده است.', { date: formatGregorianDate(selectedCalDate, lang, 'medium') })}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {reminders.filter(r => r.date === selectedCalDate).map(rem => (
                  <ReminderCard 
                    key={rem.id} 
                    reminder={rem} 
                    getCategoryMeta={getCategoryMeta} 
                    getPriorityBadge={getPriorityBadge}
                    onToggleStatus={() => toggleReminderStatus(rem.id)}
                    onEdit={() => handleOpenEdit(rem)}
                    onDelete={() => deleteReminder(rem.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* VIEW 3: Categories Breakdown */}
      {activeTab === 'categories' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          {(['birthday', 'event', 'bills', 'school', 'medical', 'shopping', 'personal', 'family'] as ReminderCategory[]).map(cat => {
            const meta = getCategoryMeta(cat);
            const Icon = meta.icon;
            const catItems = reminders.filter(r => r.category === cat);

            return (
              <div 
                key={cat} 
                className={`rounded-3xl p-5 border shadow-sm space-y-4 ${meta.bg}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-2xl ${meta.badge}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className={`text-base font-extrabold font-display ${meta.text}`}>
                        {meta.name}
                      </h3>
                      <p className="text-xs opacity-80">
                        {t('reminderRegisteredCount', '{{count}} یادآور ثبت‌شده', { count: toPersianDigits(catItems.length) })}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => handleOpenAdd(cat)}
                    className={`p-2 rounded-xl text-xs font-bold flex items-center gap-1 bg-white/80 dark:bg-black/20 hover:bg-white transition-all cursor-pointer ${meta.text}`}
                  >
                    <Plus className="w-4 h-4" />
                    <span>{t('reminderNewItem', 'جدید')}</span>
                  </button>
                </div>

                <div className="space-y-2 pt-2 border-t border-black/5 dark:border-white/5">
                  {catItems.length === 0 ? (
                    <p className="text-xs opacity-60 text-center py-4">
                      {t('reminderNoInCat', 'هیچ یادآوری در این دسته وجود ندارد.')}
                    </p>
                  ) : (
                    catItems.map(rem => (
                      <ReminderCard 
                        key={rem.id} 
                        reminder={rem} 
                        getCategoryMeta={getCategoryMeta} 
                        getPriorityBadge={getPriorityBadge}
                        onToggleStatus={() => toggleReminderStatus(rem.id)}
                        onEdit={() => handleOpenEdit(rem)}
                        onDelete={() => deleteReminder(rem.id)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 p-5 sm:p-6 space-y-5 animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 rounded-2xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                  <Bell className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-bold font-display text-gray-900 dark:text-white">
                    {editingReminder ? t('reminderEditTitle', 'ویرایش یادآور') : t('reminderNewTitle', 'ایجاد یادآور جدید')}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {t('reminderModalDesc', 'تنظیم زمان‌بندی، اولویت و عضو مربوطه در خانواده')}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Quick Presets for Rapid Creation */}
            {!editingReminder && (
              <div className="space-y-1.5">
                <span className="text-[11px] font-semibold text-gray-400 block">{t('reminderQuickPresets', 'پیشنهادهای سریع:')}</span>
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                  <button
                    type="button"
                    onClick={() => handleQuickPreset(t('reminderPreset1Title', 'تولد و سالگرد ازدواج'), 'birthday', 'yearly')}
                    className="px-2.5 py-1 rounded-lg bg-fuchsia-50 text-fuchsia-700 text-xs font-medium shrink-0 hover:bg-fuchsia-100"
                  >
                    🎂 {t('reminderPreset1Name', 'تولد و سالگرد')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickPreset(t('reminderPreset2Title', 'پرداخت قبض برق و گاز'), 'bills', 'monthly')}
                    className="px-2.5 py-1 rounded-lg bg-teal-50 text-teal-700 text-xs font-medium shrink-0 hover:bg-teal-100"
                  >
                    💳 {t('reminderPreset2Name', 'پرداخت قبوض')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickPreset(t('reminderPreset3Title', 'جلسه اولیا و مربیان مدرسه'), 'school', 'none')}
                    className="px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 text-xs font-medium shrink-0 hover:bg-violet-100"
                  >
                    🎓 {t('reminderPreset3Name', 'مدرسه / دانشگاه')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickPreset(t('reminderPreset4Title', 'تست خون تیرویید و آزمایش'), 'medical', 'none')}
                    className="px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700 text-xs font-medium shrink-0 hover:bg-rose-100"
                  >
                    🩺 {t('reminderPreset4Name', 'آزمایش تیرویید')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickPreset(t('reminderPreset5Title', 'پیگیری مرسوله پستی سفارش'), 'shopping', 'none')}
                    className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-medium shrink-0 hover:bg-amber-100"
                  >
                    🛍️ {t('reminderPreset5Name', 'پیگیری سفارش')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleQuickPreset(t('reminderPreset6Title', 'ملاقات خانوادگی و مهمانی'), 'event', 'none')}
                    className="px-2.5 py-1 rounded-lg bg-sky-50 text-sky-700 text-xs font-medium shrink-0 hover:bg-sky-100"
                  >
                    📅 {t('reminderPreset6Name', 'رویداد خانوادگی')}
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                  {t('reminderFormTitleLabel', 'عنوان یادآور')} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder={t('reminderFormTitlePh', 'مثلاً: تولد همسر / پرداخت قبض / آزمایش تیرویید...')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Category & Priority Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Category */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t('reminderFormCatLabel', 'دسته‌بندی')}
                  </label>
                  <select
                    value={category}
                    onChange={(e) => {
                      const newCat = e.target.value as ReminderCategory;
                      setCategory(newCat);
                      if (newCat === 'birthday' && !editingReminder) {
                        setRepeat('yearly');
                      }
                    }}
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="birthday">🎂 {t('reminderCatBirthday', 'تولد و سالگردها')}</option>
                    <option value="event">📅 {t('reminderCatEvent', 'رویداد و قرار ملاقات')}</option>
                    <option value="bills">💳 {t('reminderCatBills', 'قبوض و مالی')}</option>
                    <option value="school">🎓 {t('reminderCatSchool', 'مدرسه و آموزش')}</option>
                    <option value="medical">🩺 {t('reminderCatMedical', 'پزشکی و سلامت')}</option>
                    <option value="shopping">🛍️ {t('reminderCatShopping', 'خرید و مرسولات')}</option>
                    <option value="personal">💼 {t('reminderCatPersonal', 'امور شخصی و اداری')}</option>
                    <option value="family">👨‍👩‍👧‍👦 {t('reminderCatFamily', 'مدیریت خانواده')}</option>
                  </select>
                </div>

                {/* Person */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t('reminderFormPersonLabel', 'مربوط به کدام شخص؟')}
                  </label>
                  <select
                    value={person}
                    onChange={(e) => setPerson(e.target.value)}
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {availablePersons.map(p => (
                      <option key={p} value={p}>👤 {p}</option>
                    ))}
                    <option value="custom">✏️ {t('reminderFormCustomPerson', 'شخص جدید / سفارشی...')}</option>
                  </select>
                </div>
              </div>

              {/* Custom Person Input if selected */}
              {person === 'custom' && (
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t('reminderFormCustomPersonLabel', 'نام شخص سفارشی')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('reminderFormCustomPersonPh', 'مثلاً: علی / مریم')}
                    value={customPerson}
                    onChange={(e) => setCustomPerson(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              {/* Date & Time Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t('reminderFormDate', 'تاریخ')}
                  </label>
                  <input
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t('reminderFormTime', 'ساعت')}
                  </label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Priority & Repeat Options */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t('reminderFormPri', 'اولویت')}
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as ReminderPriority)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="high">🔴 {t('reminderPriHighText', 'فوری / بالا')}</option>
                    <option value="medium">🟡 {t('reminderPriMedText', 'متوسط')}</option>
                    <option value="low">🔵 {t('reminderPriLowText', 'عادی')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    {t('reminderFormRepeat', 'تکرار')}
                  </label>
                  <select
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value as ReminderRepeat)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="none">{t('reminderRepeatNone', 'بدون تکرار')}</option>
                    <option value="daily">{t('reminderRepeatDaily', 'روزانه')}</option>
                    <option value="weekly">{t('reminderRepeatWeekly', 'هفتگی')}</option>
                    <option value="monthly">{t('reminderRepeatMonthly', 'ماهانه')}</option>
                    <option value="yearly">{t('reminderRepeatYearly', 'سالانه')}</option>
                  </select>
                </div>
              </div>

              {/* Smart Reminder Offsets Configuration */}
              <div className="space-y-3 bg-indigo-50/60 dark:bg-indigo-950/30 p-3.5 sm:p-4 rounded-2xl border border-indigo-100 dark:border-indigo-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <label className="text-xs font-bold text-gray-900 dark:text-white">
                      {t('reminderOffsetsTitle', 'زمان‌بندی اعلان و یادآوری قبل از سررسید')}
                    </label>
                  </div>
                  <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-lg border border-indigo-100 dark:border-indigo-900">
                    {offsets.length > 0 ? t('reminderActiveCount', '{{count}} زمان یادآوری فعال', { count: formatNumberByLang(offsets.length, lang) }) : t('reminderNoOffsets', 'بدون یادآوری')}
                  </span>
                </div>

                {/* Active Configured Offsets with Computed Exact Gregorian Notification Times */}
                {offsets.length > 0 && (
                  <div className="space-y-1.5 max-h-36 overflow-y-auto pr-0.5">
                    {offsets.map((offset, idx) => {
                      const notifTime = calculateNotificationTime(date, time, offset);
                      return (
                        <div
                          key={`${offset.value}-${offset.unit}-${idx}`}
                          className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 rounded-xl border border-indigo-100 dark:border-indigo-900/60 shadow-2xs"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                            <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                              {formatReminderOffset(offset, lang, t)}
                            </span>
                            <span className="text-[11px] text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-lg font-medium border border-indigo-100/80 dark:border-indigo-900/40 dir-ltr">
                              ⏰ {notifTime.fullFormatted}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveOffset(idx)}
                            className="p-1 text-gray-400 hover:text-rose-500 dark:hover:text-rose-400 rounded-lg transition-colors cursor-pointer shrink-0"
                            title={t('reminderRemoveOffsetBtn', 'حذف این زمان یادآوری')}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Preset Fast Buttons */}
                <div>
                  <span className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 mb-1.5">
                    {t('reminderPresetsLabel', 'افزودن سریع زمان‌های متداول:')}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { value: 0, unit: 'minutes' as const, label: t('presetAtTime', 'همان لحظه') },
                      { value: 15, unit: 'minutes' as const, label: t('preset15m', '۱۵ دقیقه قبل') },
                      { value: 1, unit: 'hours' as const, label: t('preset1h', '۱ ساعت قبل') },
                      { value: 1, unit: 'days' as const, label: t('preset1d', '۱ روز قبل') },
                      { value: 3, unit: 'days' as const, label: t('preset3d', '۳ روز قبل') },
                      { value: 1, unit: 'weeks' as const, label: t('preset1w', '۱ هفته قبل') },
                    ].map((preset) => {
                      const isSelected = offsets.some(o => o.value === preset.value && o.unit === preset.unit);
                      return (
                        <button
                          key={`${preset.value}-${preset.unit}`}
                          type="button"
                          onClick={() => handleToggleOffset(preset)}
                          className={`px-2.5 py-1 rounded-xl text-xs font-medium transition-all cursor-pointer flex items-center gap-1 ${
                            isSelected
                              ? 'bg-indigo-600 text-white font-bold shadow-xs'
                              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                          <span>{preset.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Custom Offset Builder */}
                <div className="pt-2 border-t border-indigo-100 dark:border-indigo-900/40 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 shrink-0">
                    {t('reminderCustomOffset', 'زمان سفارشی:')}
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={customOffsetValue}
                    onChange={(e) => setCustomOffsetValue(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 px-2.5 py-1.5 bg-white dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-center font-bold"
                  />
                  <select
                    value={customOffsetUnit}
                    onChange={(e) => setCustomOffsetUnit(e.target.value as ReminderOffsetUnit)}
                    className="px-2.5 py-1.5 bg-white dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white font-medium"
                  >
                    <option value="minutes">{t('unitMinutes', 'دقیقه قبل')}</option>
                    <option value="hours">{t('unitHours', 'ساعت قبل')}</option>
                    <option value="days">{t('unitDays', 'روز قبل')}</option>
                    <option value="weeks">{t('unitWeeks', 'هفته قبل')}</option>
                  </select>
                  <button
                    type="button"
                    onClick={handleAddCustomOffset}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1 shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>{t('reminderAddCustomOffsetBtn', 'افزودن')}</span>
                  </button>
                </div>
              </div>

              {/* Description */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300">
                    {t('reminderFormDesc', 'توضیحات تکمیلی (اختیاری)')}
                  </label>
                  <VoiceInputButton
                    currentValue={description}
                    onUpdate={(val) => setDescription(val)}
                    title={t('voiceInputNormal', 'ورودی صوتی')}
                  />
                </div>
                <textarea
                  rows={2}
                  placeholder={t('reminderFormDescPh', 'آدرس کلینیک، کد مرسوله، یادداشت‌های ضروری...')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 py-3 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs sm:text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <Check className="w-4 h-4" />
                  <span>{editingReminder ? t('reminderFormSubmitEdit', 'ذخیره تغییرات') : t('reminderFormSubmitAdd', 'ثبت یادآور')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="py-3 px-4 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold text-xs sm:text-sm hover:bg-gray-200 cursor-pointer"
                >
                  {t('reminderFormCancel', 'انصراف')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MANAGE FAMILY MEMBERS MODAL */}
      {showFamilyModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 w-full max-w-md rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 p-6 space-y-5 animate-in fade-in duration-200">
            <div className="flex items-center justify-between border-b pb-4 border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 rounded-2xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold font-display text-gray-900 dark:text-white">
                    {t('reminderMembersTitle', 'مدیریت اعضای خانواده')}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {t('reminderMembersDesc', 'تعریف، افزودن یا حذف افراد برای تخصیص یادآورها')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowFamilyModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form to Add New Member */}
            <form onSubmit={handleAddFamilyMember} className="flex gap-2">
              <input
                type="text"
                placeholder={t('reminderMembersPh', 'نام فرد جدید (مثلاً: بنیتا، مریم، علی)...')}
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                className="flex-1 px-3.5 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl text-xs sm:text-sm border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                type="submit"
                disabled={!newMemberName.trim()}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer shrink-0"
              >
                <Plus className="w-4 h-4" />
                <span>{t('reminderMembersAdd', 'افزودن')}</span>
              </button>
            </form>

            {/* List of current Family Members */}
            <div className="space-y-2">
              <span className="text-xs font-bold text-gray-700 dark:text-gray-300 block">
                {t('reminderMembersCurrent', 'اعضای فعلی ثبت‌شده ({{count}}):', { count: toPersianDigits(familyMembers.length) })}
              </span>
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-1">
                {familyMembers.map((member) => (
                  <div
                    key={member}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-800 dark:text-gray-200"
                  >
                    <User className="w-3.5 h-3.5 text-indigo-500" />
                    <span>{member}</span>
                    {member !== 'من' && member !== t('reminderMe', 'من') && (
                      <button
                        type="button"
                        onClick={() => handleRemoveFamilyMember(member)}
                        className="mr-1 p-0.5 text-gray-400 hover:text-rose-500 rounded-md transition-colors cursor-pointer"
                        title={t('reminderMembersRemove', 'حذف {{member}}', { member })}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100 dark:border-gray-800 flex justify-end">
              <button
                type="button"
                onClick={() => setShowFamilyModal(false)}
                className="px-5 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl text-xs font-bold hover:opacity-90 transition-opacity cursor-pointer"
              >
                {t('reminderMembersClose', 'تأیید و بستن')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Subcomponent for individual reminder item card
interface ReminderCardProps {
  reminder: ReminderItem;
  getCategoryMeta: (cat: ReminderCategory) => any;
  getPriorityBadge: (p: ReminderPriority) => React.ReactNode;
  onToggleStatus: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function ReminderCard({ reminder, getCategoryMeta, getPriorityBadge, onToggleStatus, onEdit, onDelete }: ReminderCardProps) {
  const { lang, t } = useLanguage();
  const meta = getCategoryMeta(reminder.category);
  const Icon = meta.icon;
  const isCompleted = reminder.status === 'completed';

  return (
    <div className={`p-4 rounded-2xl border transition-all relative group flex flex-col justify-between ${
      isCompleted 
        ? 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 opacity-60' 
        : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md'
    }`}>
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleStatus}
              className="text-gray-400 hover:text-emerald-600 transition-colors cursor-pointer shrink-0"
            >
              {isCompleted ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500 fill-emerald-500/20" />
              ) : (
                <Circle className="w-5 h-5 text-gray-300 dark:text-gray-600 hover:text-indigo-500" />
              )}
            </button>

            <span className={`text-sm font-bold text-gray-900 dark:text-white ${isCompleted ? 'line-through text-gray-400' : ''}`}>
              {reminder.title}
            </span>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {getPriorityBadge(reminder.priority)}
          </div>
        </div>

        {reminder.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 pr-7 line-clamp-2">
            {reminder.description}
          </p>
        )}

        {/* Reminder Offsets Badges */}
        {reminder.reminders && reminder.reminders.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1 pr-7">
            <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 flex items-center gap-1 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-md border border-indigo-100 dark:border-indigo-900/50">
              <Bell className="w-3 h-3 text-indigo-500" />
              <span>
                {reminder.reminders.map(o => formatReminderOffset(o, lang, t)).join(lang === 'fa' ? '، ' : ', ')}
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="mt-3 pt-2.5 border-t border-gray-100 dark:border-gray-800/60 flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold flex items-center gap-1 ${meta.badge}`}>
            <Icon className="w-3 h-3" />
            <span>{meta.name}</span>
          </span>

          <span className="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-[10px] font-medium flex items-center gap-1">
            <User className="w-3 h-3 text-indigo-500" />
            <span>{reminder.person}</span>
          </span>

          <span className="flex items-center gap-1 text-[11px] text-gray-500 font-mono">
            <Clock className="w-3 h-3 text-amber-500" />
            <span>{formatGregorianDate(reminder.date, lang, 'medium')} {reminder.time ? t('reminderCardTime', 'ساعت {{time}}', { time: reminder.time }) : ''}</span>
          </span>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <a
            href={createReminderGoogleCalendarUrl(reminder)}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-1 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/60 dark:hover:bg-indigo-900/80 text-indigo-700 dark:text-indigo-300 rounded-lg transition-colors flex items-center gap-1 text-[11px] font-bold"
            title={t('reminderCardAddGcal', 'افزودن مستقیم این یادآور به گوگل کلندر (Google Calendar)')}
          >
            <CalendarIcon className="w-3.5 h-3.5 text-indigo-500" />
            <span className="hidden sm:inline">{t('reminderCardGcal', 'گوگل کلندر')}</span>
            <ExternalLink className="w-3 h-3 text-indigo-400" />
          </a>

          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            title={t('reminderCardEdit', 'ویرایش')}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
            title={t('reminderCardDelete', 'حذف')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
